export interface DownloadFileLinkLike {
  file_name?: string | null
  url?: string | null
}

export interface DownloadLinkMetadata {
  count: number
  extensions: string[]
  fileNames: string[]
  hasFiles: boolean
  label: string
  tooltipLine: string
}

const UNKNOWN_EXTENSION = 'unknown extension'

const getExtensionFromValue = (value?: string | null): string | null => {
  const rawValue = (value || '').trim()
  if (!rawValue) return null

  const withoutQuery = rawValue.split(/[?#]/)[0] || rawValue
  const rawFilename = withoutQuery.split('/').pop() || withoutQuery
  let filename = rawFilename

  try {
    filename = decodeURIComponent(rawFilename)
  } catch {
    filename = rawFilename
  }

  const dotIndex = filename.lastIndexOf('.')
  if (dotIndex < 0 || dotIndex === filename.length - 1) return null

  return `.${filename.slice(dotIndex + 1).trim().toLowerCase()}`
}

const getFileLinkExtension = (fileLink: DownloadFileLinkLike): string => {
  return getExtensionFromValue(fileLink.file_name)
    || getExtensionFromValue(fileLink.url)
    || UNKNOWN_EXTENSION
}

const getFilenameFromValue = (value?: string | null): string | null => {
  const rawValue = (value || '').trim()
  if (!rawValue) return null

  const withoutQuery = rawValue.split(/[?#]/)[0] || rawValue
  const rawFilename = withoutQuery.split('/').pop() || withoutQuery
  if (!rawFilename) return null

  try {
    return decodeURIComponent(rawFilename)
  } catch {
    return rawFilename
  }
}

const getFileLinkName = (fileLink: DownloadFileLinkLike): string => {
  return getFilenameFromValue(fileLink.file_name)
    || getFilenameFromValue(fileLink.url)
    || 'Unknown file'
}

const getDownloadableLinks = (fileLinks: unknown): DownloadFileLinkLike[] => {
  if (!Array.isArray(fileLinks)) return []

  return fileLinks.filter((fileLink): fileLink is DownloadFileLinkLike => (
    typeof fileLink?.url === 'string' && fileLink.url.trim().length > 0
  ))
}

export const getDownloadLinkMetadata = (
  fileLinks: unknown,
  typeLabel: string,
  options: { showSingleCount?: boolean } = {},
): DownloadLinkMetadata => {
  const downloadableLinks = getDownloadableLinks(fileLinks)
  const extensions = downloadableLinks.map(getFileLinkExtension)
  const fileNames = downloadableLinks.map(getFileLinkName)
  const count = extensions.length
  const shouldShowCount = count > 1 || (options.showSingleCount && count > 0)

  return {
    count,
    extensions,
    fileNames,
    hasFiles: count > 0,
    label: shouldShowCount ? `Download (${count})` : 'Download',
    tooltipLine: count
      ? `${typeLabel} ${count === 1 ? 'file' : 'files'}: ${extensions.join(', ')}`
      : '',
  }
}
